import React, { useEffect, useState } from "react";
import Turnstile from "react-turnstile";
import { motion } from "framer-motion";
import toast from "react-hot-toast";
import { Helmet } from "react-helmet";

export default function Main() {
  const [user, setUser] = useState({
    vorname: "",
    nachname: "",
    email: "",
    nachricht: "",
  });
  const [cfToken, setCfToken] = useState("");
  const [sending, setSending] = useState(false);
  const [tsKey, setTsKey] = useState(0);

  function handleInputChange(e) {
    const { name, value } = e.target;
    setUser((prev) => ({ ...prev, [name]: value }));
  }

  const canSend =
    !!cfToken &&
    !!user.vorname &&
    !!user.nachname &&
    !!user.email &&
    !!user.nachricht;

  async function handleSubmit(e) {
    e.preventDefault?.();
    if (!canSend) return;

    setSending(true);
    try {
      const res = await fetch(`${process.env.BACKEND_URL}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...user, cf_token: cfToken }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || `HTTP ${res.status}`);
      }

      setUser({
        vorname: "",
        nachname: "",
        email: "",
        nachricht: "",
      });
      setCfToken("");
      setTsKey((k) => k + 1);
      toast.success("Nachricht gesendet");
    } catch (err) {
      console.error(err);
      toast.error("Fehler beim Senden der Nachricht");
    } finally {
      setSending(false);
    }
  }

  return (
    <>
      <Helmet>
        <title>Kontakt | Kaminfeger Knapp in Buchs | Termin anfragen</title>
        <meta
          name="description"
          content="Kontakt zu Kaminfeger Knapp in Buchs: Telefon, E-Mail und Kontaktformular für Terminvereinbarungen, Offerten und Fragen rund um Brandschutz und Feuerung."
        />
      </Helmet>

      <main>
        <div className="kn-page mt-8">
          <div className="flex justify-center">
            <h1 className="kn-h1">Kontakt</h1>
          </div>

          <div className="flex flex-col p-4 justify-center lg:flex-row items-center lg:justify-center lg:items-start mt-16 gap-16">
            {/*Kontaktinformationen*/}
            <div className="max-w-md w-full">
              <p className="kn-h3">Kaminfegermeister Marco Knapp</p>
              <p className="kn-legend mt-8">Adresse</p>
              <p className="kn-body">Kaminfegergeschäft Knapp Marco</p>
              <p className="kn-body">Schützenhausweg 8</p>
              <p className="kn-body">9470 Buchs</p>

              <p className="kn-legend mt-6">Telefon</p>
              <p className="kn-body">Natel +41 79 814 81 32</p>

              <p className="kn-legend mt-6">Email</p>
              <p className="kn-body">kaminfeger-knapp@gmx.ch</p>
            </div>

            {/*Kontaktformular*/}
            <form
              onSubmit={handleSubmit}
              className="flex flex-col w-full max-w-lg space-y-4 ml-0 lg:ml-16"
            >
              <h2 className="kn-h3 mb-4">Schicken Sie uns eine E-mail</h2>

              <div className="flex flex-col sm:flex-row gap-4 ">
                <div className="w-full">
                  <label htmlFor="vn" className="sr-only">
                    Vorname
                  </label>
                  <input
                    onChange={handleInputChange}
                    name="vorname"
                    value={user.vorname}
                    className="kn-input"
                    placeholder="Vorname"
                    type="text"
                    id="vn"
                    autoComplete="given-name"
                  ></input>
                </div>
                <div className="w-full">
                  <label htmlFor="nn" className="sr-only">
                    Nachname
                  </label>
                  <input
                    onChange={handleInputChange}
                    name="nachname"
                    value={user.nachname}
                    className="kn-input"
                    placeholder="Nachname"
                    type="text"
                    id="nn"
                    autoComplete="family-name"
                  ></input>
                </div>
              </div>

              <label htmlFor="email" className="sr-only">
                E-Mail
              </label>
              <input
                onChange={handleInputChange}
                value={user.email}
                name="email"
                className="kn-input"
                placeholder="Email"
                type="email"
                id="email"
                autoComplete="email"
              ></input>

              <label htmlFor="nachricht" className="sr-only">
                Nachricht
              </label>
              <textarea
                onChange={handleInputChange}
                value={user.nachricht}
                name="nachricht"
                className="kn-input"
                id="nachricht"
                placeholder="Ihre Nachricht an uns"
                rows={5}
              ></textarea>

              <div className="mt-2">
                <Turnstile
                  key={tsKey}
                  sitekey={process.env.TURNSTILE_SITE_KEY}
                  onVerify={(token) => setCfToken(token)}
                  onExpire={() => setCfToken("")}
                  onError={() => setCfToken("")}
                  options={{ theme: "light" }}
                />
              </div>

              <motion.button
                type="submit"
                disabled={!canSend || sending}
                className={`kn-btn-primary w-full mt-4 ${
                  !canSend || sending ? "opacity-50 cursor-not-allowed" : ""
                }`}
                initial={{ opacity: 0, y: 0 }}
                whileHover={{ scale: 1.02 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
              >
                {sending ? "Senden..." : "Senden"}
              </motion.button>
            </form>
          </div>

          {/*Map*/}
          <div className="mt-16 flex justify-center ">
            <iframe
              title="Standort Kaminfeger Knapp, Buchs (Google Maps)"
              className="max-w-3xl rounded-md shadow-md"
              width="100%"
              height="400"
              frameBorder="0"
              marginHeight="0"
              marginWidth="0"
              loading="lazy"
              src="https://maps.google.com/maps?width=520&amp;height=400&amp;hl=en&amp;q=Sch%C3%BCtzenhausweg%208%209470%20Buchs+(Kaminfegermeister%20Marco%20Knapp)&amp;t=&amp;z=14&amp;ie=UTF8&amp;iwloc=B&amp;output=embed"
            ></iframe>
          </div>
        </div>
      </main>
    </>
  );
}
